/**
 * Floating damage / heal numbers (DESIGN 6.2 layer 8, 6.4): white for
 * physical, element colour otherwise, green "+N" for heals, red with "!" for
 * friendly fire, a x2 / x3 badge for wet / frozen crits. Numbers rise 0.6 u
 * over 0.8 s and fade in the last 40%.
 *
 * Beam and spray ticks arrive at 10 Hz; hits on the same target within
 * AGGREGATE_S are summed into one growing number so the screen stays
 * readable (the number grows with the total, which also teaches "this combo
 * is big").
 */

import type { DamageType } from '@shared/index.js';
import { damageColor, ELEMENT_SWATCH, UI } from '../palette.js';
import { easeOutCubic, font, outlinedText, UNIT, type Ctx } from './gfx.js';
import type { Camera } from './camera.js';

const POOL = 64;
const DURATION = 0.8;
const RISE_UNITS = 0.6;
const AGGREGATE_S = 0.25;

interface FloatNum {
  active: boolean;
  x: number;
  y: number;
  z: number;
  t: number;
  value: number;
  text: string;
  color: string;
  size: number;
  badge: string;
  badgeColor: string;
  ff: boolean;
  tgt: number;
  /** Random horizontal drift in px so stacked numbers do not overlap exactly. */
  drift: number;
}

function blank(): FloatNum {
  return { active: false, x: 0, y: 0, z: 0, t: 0, value: 0, text: '', color: UI.white, size: 13, badge: '', badgeColor: UI.white, ff: false, tgt: -1, drift: 0 };
}

export class Numbers {
  private readonly pool: FloatNum[] = [];
  /** Target id -> pool index of the number still accepting aggregation. */
  private readonly byTarget = new Map<number, number>();
  private seed = 7;

  constructor() {
    for (let i = 0; i < POOL; i++) this.pool.push(blank());
  }

  private rnd(): number {
    this.seed = (this.seed * 1664525 + 1013904223) >>> 0;
    return this.seed / 4294967296;
  }

  /** Oldest-eviction allocation. */
  private alloc(): FloatNum {
    let oldest: FloatNum | null = null;
    for (const n of this.pool) {
      if (!n.active) return n;
      if (!oldest || n.t > oldest.t) oldest = n;
    }
    const o = oldest as FloatNum;
    this.byTarget.forEach((idx, key) => { if (this.pool[idx] === o) this.byTarget.delete(key); });
    return o;
  }

  private start(n: FloatNum, x: number, y: number, z: number, tgt: number): void {
    n.active = true;
    n.x = x; n.y = y; n.z = z;
    n.t = 0;
    n.tgt = tgt;
    n.badge = '';
    n.ff = false;
    n.drift = (this.rnd() - 0.5) * 18;
  }

  /**
   * Damage hit. `mult` 2 or 3 adds the crit badge; `ff` paints it red with a
   * "!" (DESIGN 6.4). Hits on the same target within AGGREGATE_S merge.
   */
  hit(tgt: number, dmg: number, type: DamageType, x: number, y: number, z: number, mult: 0 | 2 | 3, ff: boolean): void {
    const idx = this.byTarget.get(tgt);
    let n = idx === undefined ? null : this.pool[idx]!;
    if (n && n.active && n.t < AGGREGATE_S && !n.text.startsWith('+')) {
      n.value += dmg;
      n.x = x; n.y = y;
    } else {
      n = this.alloc();
      this.start(n, x, y, z, tgt);
      n.value = dmg;
      this.byTarget.set(tgt, this.pool.indexOf(n));
    }
    if (ff) n.ff = true;
    if (mult === 2) { n.badge = 'x2'; n.badgeColor = ELEMENT_SWATCH.lightning.core; }
    else if (mult === 3) { n.badge = 'x3'; n.badgeColor = ELEMENT_SWATCH.ice.core; }
    n.color = n.ff ? UI.ff : damageColor(type);
    n.text = n.ff ? `${Math.round(n.value)}!` : `${Math.round(n.value)}`;
    n.size = 13 * Math.min(2.2, Math.max(1, n.value / 150));
  }

  heal(tgt: number, amt: number, x: number, y: number, z: number): void {
    const idx = this.byTarget.get(tgt);
    let n = idx === undefined ? null : this.pool[idx]!;
    if (n && n.active && n.t < AGGREGATE_S && n.text.startsWith('+')) {
      n.value += amt;
      n.x = x; n.y = y;
    } else {
      n = this.alloc();
      this.start(n, x, y, z, tgt);
      n.value = amt;
      this.byTarget.set(tgt, this.pool.indexOf(n));
    }
    n.color = UI.hpGreen;
    n.text = `+${Math.round(n.value)}`;
    n.size = 13 * Math.min(1.8, Math.max(1, n.value / 200));
  }

  /** Free-form pop ("Wet!", "Frozen", magick names). Never aggregated. */
  text(x: number, y: number, z: number, text: string, color: string, size = 14): void {
    const n = this.alloc();
    this.start(n, x, y, z, -1);
    n.value = 0;
    n.text = text;
    n.color = color;
    n.size = size;
  }

  /** Mark the latest number on a target as friendly fire (the `ff` event may arrive after its `hit`). */
  markFriendlyFire(tgt: number): void {
    const idx = this.byTarget.get(tgt);
    if (idx === undefined) return;
    const n = this.pool[idx]!;
    if (!n.active || n.text.startsWith('+')) return;
    n.ff = true;
    n.color = UI.ff;
    n.text = `${Math.round(n.value)}!`;
  }

  step(dt: number): void {
    for (const n of this.pool) {
      if (!n.active) continue;
      n.t += dt;
      if (n.t >= DURATION) {
        n.active = false;
        if (this.byTarget.get(n.tgt) === this.pool.indexOf(n)) this.byTarget.delete(n.tgt);
      }
    }
  }

  draw(ctx: Ctx, cam: Camera): void {
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const n of this.pool) {
      if (!n.active) continue;
      const f = n.t / DURATION;
      const rise = easeOutCubic(f) * RISE_UNITS * UNIT;
      const px = cam.sx(n.x) + n.drift;
      const py = cam.sy(n.y) - n.z * UNIT - rise;
      if (px < -40 || py < -40 || px > cam.viewW + 40 || py > cam.viewH + 40) continue;
      const alpha = f > 0.6 ? 1 - (f - 0.6) / 0.4 : 1;
      // pop in over the first 80 ms
      const pop = n.t < 0.08 ? 0.6 + (n.t / 0.08) * 0.4 : 1;
      ctx.globalAlpha = alpha;
      ctx.font = font(n.size * pop, 800);
      outlinedText(ctx, n.text, px, py, n.color, n.badge ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.85)', n.badge ? 4 : 3);
      if (n.badge) {
        const w = ctx.measureText(n.text).width;
        ctx.font = font(Math.max(10, n.size * 0.7), 800);
        outlinedText(ctx, n.badge, px + w / 2 + 10, py - n.size * 0.35, n.badgeColor);
      }
    }
    ctx.globalAlpha = 1;
  }
}
